import { useCrudService } from 'framework/crud';
import { ListResponse, LoadingParams } from 'framework/list';
import { usePeopleManagementApi } from 'infrastructure/api';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { DEFAULT_ERROR_MESSAGE } from 'framework/form';
import { isTechnologyRequired, useTechnologyService } from 'domain/shared/career';
import { useBusinessFunctionService } from 'domain/shared/businessFunction';
import { useOfficeService, usePositionService } from 'domain/shared/employee';
import useVacancyReferenceService from 'domain/shared/newVacancy/useVacancyReferenceService';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import { DEFAULT_POSITION_SLOT_STATUS_FILTER } from './constants';
import { PositionSlot, PositionSlotListItem } from './types';
import { enrichPositionSlots } from './utils/positionSlotDisplay';
import { getPositionSlotSort, prioritizePendingPositionSlots } from './utils/positionSlotSort';

const RESOURCE_URL = '/position-slots';

const usePositionSlotsService = () => {
  const { addMessage } = useMessageService();
  const { sendPatchRequest } = usePeopleManagementApi();
  const { findVacancyGroupReferences } = useVacancyReferenceService();
  const { findAll: findAllTechnologies } = useTechnologyService();
  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllOffices } = useOfficeService();
  const { findAll: findAllBusinessFunctions } = useBusinessFunctionService();
  const { search, searchAll, create, update, getById } = useCrudService<PositionSlot, ListResponse<PositionSlot>>({
    listResourceUrl: RESOURCE_URL,
    singleResourceUrl: `${RESOURCE_URL}/:id`,
    useService: usePeopleManagementApi,
  });

  const enrichPositionSlotList = async (positionSlots: PositionSlot[]): Promise<PositionSlotListItem[]> => {
    const [positions, technologies, offices, businessFunctions, vacancyGroupByVacancyId] = await Promise.all([
      findAllPositions(),
      findAllTechnologies(),
      findAllOffices(),
      findAllBusinessFunctions(),
      findVacancyGroupReferences(positionSlots.map(positionSlot => positionSlot.vacancyId)),
    ]);

    return enrichPositionSlots(positionSlots, {
      positions,
      technologies,
      offices,
      businessFunctions,
      vacancyGroupByVacancyId,
    });
  };

  const buildCreatePayload = (data: FormFieldsData) => {
    const specializationId = Number(data.specializationId);

    return {
      specializationId,
      technologyId: isTechnologyRequired(specializationId) ? (data.technologyId as string) || null : null,
      competencyLevel: (data.competencyLevel as string) || null,
      officeIds: ((data.officeIds as string[]) || []).map(Number),
      workerType: data.workerType,
      workRateType: data.workRateType,
      businessFunctionId: data.businessFunctionId,
      positionProfileId: data.positionProfileId || null,
    };
  };

  const getPositionSlotsListDataMethod = async (loadParams: LoadingParams) => {
    try {
      const statusFilter = loadParams.filter?.status;

      const response = await searchAll({
        ...loadParams,
        filter: {
          ...loadParams.filter,
          status: statusFilter || {
            in: DEFAULT_POSITION_SLOT_STATUS_FILTER,
          },
        },
        sort: getPositionSlotSort(loadParams.sort),
        pageSize: 100,
      });

      const positionSlots = prioritizePendingPositionSlots(await enrichPositionSlotList(response || []));

      return {
        result: positionSlots,
        totalPages: 1,
        totalElements: positionSlots.length,
      };
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));

      return {
        result: [],
        totalPages: 1,
        totalElements: 0,
      };
    }
  };

  const getPositionSlotsPage = async (loadParams: LoadingParams) => {
    try {
      const statusFilter = loadParams.filter?.status;

      const response = await search({
        ...loadParams,
        filter: {
          ...loadParams.filter,
          status: statusFilter || {
            in: DEFAULT_POSITION_SLOT_STATUS_FILTER,
          },
        },
        sort: getPositionSlotSort(loadParams.sort),
      });

      const positionSlots = prioritizePendingPositionSlots(await enrichPositionSlotList(response.result || []));

      return {
        result: positionSlots,
        pageNumber: response.pageNumber,
        totalPages: response.totalPages,
        totalElements: response.totalElements ?? positionSlots.length,
      };
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));

      return {
        result: [],
        pageNumber: loadParams.pageNumber,
        totalPages: 1,
        totalElements: 0,
      };
    }
  };

  const getPositionSlot = async (positionSlotId: string): Promise<PositionSlotListItem | null> => {
    try {
      const positionSlot = await getById(positionSlotId);
      const [enrichedPositionSlot] = await enrichPositionSlotList([positionSlot]);

      return enrichedPositionSlot;
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));

      return null;
    }
  };

  const createPositionSlot = async (data: FormFieldsData) => create(buildCreatePayload(data));

  const updatePositionSlot = async (positionSlot: PositionSlot, data: FormFieldsData) =>
    update(positionSlot.id, {
      specializationId: positionSlot.specializationId,
      technologyId: positionSlot.technologyId,
      competencyLevel: positionSlot.competencyLevel,
      officeIds: positionSlot.officeIds,
      workerType: positionSlot.workerType,
      workRateType: data.workRateType,
      businessFunctionId: positionSlot.businessFunctionId,
      positionProfileId: positionSlot.positionProfileId ?? null,
      isMarkedForDeletion: data.isMarkedForDeletion,
    });

  const assignEmployee = async (positionSlotId: string, data: FormFieldsData) => {
    const request = await sendPatchRequest(`${RESOURCE_URL}/${positionSlotId}/assign`, {
      employeeAlias: data.employeeAlias,
    });

    return request.json();
  };

  const unassignEmployee = async (positionSlotId: string, employeeAlias: string) => {
    const request = await sendPatchRequest(`${RESOURCE_URL}/${positionSlotId}/unassign`, { employeeAlias });

    return request.json();
  };

  const archivePositionSlot = async (positionSlotId: string) => {
    const request = await sendPatchRequest(`${RESOURCE_URL}/${positionSlotId}/archive`);

    return request.json();
  };

  const confirmPositionSlot = async (positionSlotId: string) => {
    const request = await sendPatchRequest(`${RESOURCE_URL}/${positionSlotId}/confirm`);

    return request.json();
  };

  return {
    getPositionSlotsListDataMethod,
    getPositionSlotsPage,
    getPositionSlot,
    createPositionSlot,
    updatePositionSlot,
    assignEmployee,
    unassignEmployee,
    archivePositionSlot,
    confirmPositionSlot,
  };
};

export default usePositionSlotsService;
